import type { CategoryId, DailyResult } from './types'
import { encodeState, decodeState } from './codec'

const VERSION = 'v2'

function storageKey(day: string, category: CategoryId): string {
  return `lineup:${VERSION}:${day}:${category}`
}

/*
 * What we persist is deliberately answer-free. Instead of event ids (which
 * embed years) or dates, we store the player's arrangement as a permutation
 * of POSITIONS into the puzzle's deterministic starting order (`refIds`). The
 * whole blob is then obfuscated and checksummed by codec.ts.
 *
 * Stored shape (compact keys to keep the blob small):
 *   d  day key            p  permutation: positions into refIds
 *   c  category           s  status 0=playing 1=won 2=lost
 *   a  attempts used      k  per-check correctness grid
 */
interface StoredState {
  d: string
  c: CategoryId
  p: number[]
  s: 0 | 1 | 2
  a: number
  k: boolean[][]
}

const STATUS_TO_CODE: Record<DailyResult['status'], 0 | 1 | 2> = {
  playing: 0,
  won: 1,
  lost: 2,
}
const CODE_TO_STATUS: Record<0 | 1 | 2, DailyResult['status']> = {
  0: 'playing',
  1: 'won',
  2: 'lost',
}

function loadRaw(day: string, category: CategoryId): StoredState | null {
  try {
    const str = localStorage.getItem(storageKey(day, category))
    if (!str) return null
    const state = decodeState<StoredState>(str, `${day}:${category}`)
    if (!state || state.d !== day || state.c !== category) return null
    return state
  } catch {
    return null
  }
}

/**
 * Persists a result. `refIds` is the puzzle's deterministic starting order of
 * event ids; the player's order is stored as positions into it, so no ids or
 * dates ever reach localStorage.
 */
export function writeResult(result: DailyResult, refIds: string[]): void {
  const indexOf = new Map(refIds.map((id, i) => [id, i]))
  const p = result.order.map((id) => indexOf.get(id) ?? -1)
  if (p.some((i) => i < 0)) return // ids don't match this puzzle; skip

  const state: StoredState = {
    d: result.day,
    c: result.category,
    p,
    s: STATUS_TO_CODE[result.status],
    a: result.attemptsUsed,
    k: result.checks,
  }
  try {
    localStorage.setItem(
      storageKey(result.day, result.category),
      encodeState(state, `${result.day}:${result.category}`),
    )
  } catch {
    /* ignore storage failures (private mode, quota, etc.) */
  }
}

/**
 * Reads a full result, reconstructing the player's order from the stored
 * permutation and the puzzle's `refIds`. Returns null if absent, tampered, or
 * if the permutation no longer fits the puzzle (e.g. the dataset changed).
 */
export function readResult(
  day: string,
  category: CategoryId,
  refIds: string[],
): DailyResult | null {
  const state = loadRaw(day, category)
  if (!state) return null
  if (
    !Array.isArray(state.p) ||
    state.p.length !== refIds.length ||
    state.p.some((i) => i < 0 || i >= refIds.length)
  ) {
    return null
  }
  return {
    day: state.d,
    category: state.c,
    order: state.p.map((i) => refIds[i]),
    status: CODE_TO_STATUS[state.s] ?? 'playing',
    attemptsUsed: state.a,
    checks: Array.isArray(state.k) ? state.k : [],
  }
}

/** Lightweight status lookup for the home screen badges (no puzzle needed). */
export function readResultStatus(
  day: string,
  category: CategoryId,
): DailyResult['status'] | null {
  const state = loadRaw(day, category)
  if (!state) return null
  return CODE_TO_STATUS[state.s] ?? null
}
